import { useState, useEffect, useCallback } from 'react';
import { Table, Input, Skeleton, Switch, Typography, message, Space, Tag, notification, Button, Popconfirm, Segmented } from 'antd';
import type { ColumnsType, TablePaginationConfig, SorterResult, FilterValue, SortOrder } from 'antd/es/table/interface';
import { ShopOutlined, EditOutlined, PlusOutlined, CheckCircleOutlined, DeleteOutlined, FileOutlined, ClockCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import FormButton from '../components/common/formbutton';
import StoresListModal from '../components/pageversion/storeslistmodal';
import CreatePageVersionForm from '../components/pageversion/createpageversionform';
import SummaryCard from '../components/common/summarycard';
import { useAuth } from '../auth/authcontext';
import PageVersionDuplicateDrawer from '../components/pageversion/pageversionduplicatedrawer';
import { PageVersion, PageVersionValues } from '../types/pageversiontypes';
import { fetchPagedPageVersions, createPageVersion, deleteDraftPageVersion, togglePageVersionActive } from '../services/pageversionservice';

const { Search } = Input;
const { Text } = Typography;

const PageVersions = () => {
    const navigate = useNavigate();

    const [searchText, setSearchText] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [pageVersions, setPageVersions] = useState<PageVersion[]>([]);
    const [totalPageVersions, setTotalPageVersions] = useState<number>(0);
    const [publishedCount, setPublishedCount] = useState<number>(0);
    const [draftCount, setDraftCount] = useState<number>(0);
    const [pendingApprovalCount, setPendingApprovalCount] = useState<number>(0);
    const [pageVersionCount, setPageVersionCount] = useState<number>(0);
    const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState<boolean>(false);

    // State for pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    // State for sorting
    // columnKey will be the property name (e.g., 'name', 'status')
    // order will be 'ascend' or 'descend'
    const [sortedInfo, setSortedInfo] = useState<SorterResult<PageVersion>>({});

    const [messageApi, contextHolder] = message.useMessage();

    // State for the "Display Stores" modal
    const [selectedPageVersionGuid, setSelectedPageVersionGuid] = useState<string>('');
    const [storesModalVisible, setStoresModalVisible] = useState<boolean>(false);

    const { userId, isReadOnly } = useAuth();
    const userGuid = userId || null;

    const [isDuplicateDrawerVisible, setIsDuplicateDrawerVisible] = useState<boolean>(false);
    const [duplicateSourceGuid, setDuplicateSourceGuid] = useState<string>('');
    const [duplicateSourceName, setDuplicateSourceName] = useState<string>('');
    type StatusFilter = 'all' | 'active' | 'inactive';
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [togglingGuid, setTogglingGuid] = useState<string | null>(null);

    const handleToggleActive = async (guid: string) => {
        setTogglingGuid(guid);
        try {
            await togglePageVersionActive(guid);
            message.success('PageVersion status updated successfully');
            fetchPageVersions();
        }
        catch (error: any) {
            if (error?.response?.data) {
                const { message: errorMessage, errorCode } = error.response.data;

                if (errorCode === 'DUPLICATE_PAGE_VERSION_NAME') {
                    message.error(errorMessage);
                } else {
                    message.error(errorMessage || 'Failed to update status');
                }
            } else {
                message.error('Something went wrong. Please try again.');
            }
        } finally {
            setTogglingGuid(null);
        }
    };

    const fetchPageVersions = useCallback(async () => {
        setLoading(true);
        try {
            const sortOrderParam = sortedInfo.order === 'ascend' ? 'ascend' :
                sortedInfo.order === 'descend' ? 'descend' : undefined;

            const sortByParam = sortedInfo.columnKey ? String(sortedInfo.columnKey) : undefined;
            const response = await fetchPagedPageVersions(
                currentPage,
                pageSize,
                searchText,
                sortByParam,
                sortOrderParam,
                statusFilter
            );
            setPageVersions(response.data);
            setTotalPageVersions(response.totalCount);
            setPublishedCount(response.publishedCount);
            setDraftCount(response.draftCount);
            setPendingApprovalCount(response.pendingCount);
            setPageVersionCount(response.pageVersionCount);
        } catch (error) {
            console.error('Error fetching Paged Page Versions:', error);
            messageApi.error('Failed to load page versions. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchText, sortedInfo, messageApi, statusFilter]);


    // Trigger data fetch whenever pagination, search, or sort parameters change
    useEffect(() => {
        fetchPageVersions();
    }, [fetchPageVersions]); // fetchPageVersions is a dependency because it's memoized by useCallback

    // --- Pagination and Table Change Handlers ---
    const handleTableChange = (
        paginationAntd: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<PageVersion> | SorterResult<PageVersion>[],
    ) => {
        // Handle page change
        if (paginationAntd.current !== currentPage) {
            setCurrentPage(paginationAntd.current || 1);
        }
        // Handle page size change
        if (paginationAntd.pageSize !== pageSize) {
            setPageSize(paginationAntd.pageSize || 10);
            setCurrentPage(1); // Reset to first page when page size changes
        }

        // Handle sorting changes
        const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

        if (currentSorter && currentSorter.columnKey && currentSorter.order) {
            setSortedInfo({ columnKey: currentSorter.columnKey, order: currentSorter.order });
        } else {
            setSortedInfo({});
        }
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
        setSortedInfo({});
    };

    const handleDisplayStores = (pageVersion: PageVersion) => {
        setSelectedPageVersionGuid(pageVersion.guid || '');
        setStoresModalVisible(true);
    };

    const handleCloseStoresModal = () => {
        setStoresModalVisible(false);
        setSelectedPageVersionGuid('');
    };

    const commonSortDirections: SortOrder[] = ['ascend', 'descend', 'ascend'];

    // --- Table Columns ---
    const columns: ColumnsType<PageVersion> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : undefined,
            render: (text: string) => <Text>{text}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'status' ? sortedInfo.order : undefined,
            render: (status: string) => <Tag color={status === 'Finalized' ? 'success' : status === 'Pending Approval' ? 'warning' : 'processing'}>{status}</Tag>,
        },
        {
            title: 'Created Date',
            dataIndex: 'dateCreated',
            key: 'dateCreated',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'dateCreated' ? sortedInfo.order : undefined,
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
        {
            title: 'Published Date',
            dataIndex: 'publishedDate',
            key: 'publishedDate',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'publishedDate' ? sortedInfo.order : undefined,
            render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : <Tag color={'yellow'}>{'Not Set'}</Tag>),
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'startDate' ? sortedInfo.order : undefined,
            render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : <Tag color={'yellow'}>{'Not Set'}</Tag>),
        },
        {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'endDate',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'endDate' ? sortedInfo.order : undefined,
            render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : <Tag color={'yellow'}>{'Not Set'}</Tag>),
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'active' ? sortedInfo.order : undefined,
            render: (active: boolean, record) => {
                const isDisabled =
                    record.status === 'Pending Approval' ||
                    record.status === 'Draft' ||
                    isReadOnly;

                return (
                    <Popconfirm
                        title={`Are you sure you want to ${active ? 'deactivate' : 'activate'} this PageVersion?`}
                        onConfirm={() => handleToggleActive(record.guid)}
                        okText="Yes"
                        cancelText="No"
                        disabled={isDisabled}
                    >
                        <Switch
                            size="small"
                            checked={active}
                            loading={togglingGuid === record.guid}
                            disabled={isDisabled}
                        />
                    </Popconfirm>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: any, record: PageVersion) => (
                <Space>
                    <FormButton
                        title={`(${record.storeCount})`}
                        onClick={() => handleDisplayStores(record)}
                        icon={<ShopOutlined />}
                        tooltip='View Stores'
                    />
                    <FormButton
                        title={''}
                        onClick={() => navigate(`/kioskpageversion/${record.guid}`)}
                        icon={<EditOutlined />}
                        tooltip='Go to Designer'
                    />
                    {(record.status === 'Finalized' || record.status === 'Draft') && (
                        <FormButton
                            title=""
                            onClick={() => handleDuplicateClick(record)}
                            icon={<CopyOutlined />}
                            tooltip="Duplicate"
                        />
                    )}
                    {(!isReadOnly && record.status === 'Draft') && (
                        <Popconfirm
                            title={`Delete "${record.name}" draft?`}
                            description="This action cannot be undone."
                            onConfirm={async () => {
                                try {
                                    await deleteDraftPageVersion(record.guid);
                                    message.success(`Draft "${record.name}" deleted successfully`);
                                    fetchPageVersions();
                                } catch {
                                    message.error('Failed to delete draft. Please try again.');
                                }
                            }}
                            okText="Yes, Delete"
                            cancelText="No"
                            okType="danger"
                        >
                            <FormButton
                                title=""
                                icon={<DeleteOutlined />}
                                tooltip="Delete Draft"
                                onClick={() => { /* no-op, Popconfirm handles confirm */ }}
                            />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const handleCreateNewVersionClick = () => {
        setIsCreateDrawerVisible(true);
    };

    const handleCreateNewVersion = async (values: PageVersionValues): Promise<void> => {
        setIsCreateDrawerVisible(false);
        try {
            const response = await createPageVersion(values.name, userGuid!);
            notification.success({
                message: 'Success',
                description: `"${values.name}" PageVersion Created Successfully`,
            });
            navigate(`/kioskpageversion/${response.id}`);
        } catch (error) {
            console.error('Error creating page version:', error);
            notification.error({
                message: 'Error',
                description: 'An unexpected error occurred. Please try again.',
            });
        }
    };

    const cardData = [
        {
            title: 'Total',
            value: pageVersionCount,
            icon: <FileOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'total',
        },
        {
            title: 'Finalized',
            value: publishedCount,
            icon: <CheckCircleOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'finalized',
        },
        {
            title: 'Drafts',
            value: draftCount,
            icon: <EditOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'drafts',
        },
        {
            title: 'Pending Approval',
            value: pendingApprovalCount,
            icon: <ClockCircleOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'pending',
        },
    ];

    const handleDuplicateClick = (pageVersion: PageVersion) => {
        setDuplicateSourceGuid(pageVersion.guid);
        setDuplicateSourceName(pageVersion.name);
        setIsDuplicateDrawerVisible(true);
    };


    return (
        <div>
            {contextHolder}
            <Typography.Title level={2}>PageVersions</Typography.Title>
            <SummaryCard cards={cardData} loading={loading} />
            <Space direction="vertical" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Search
                        placeholder="Search page versions by name or status"
                        onSearch={handleSearch}
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                        style={{ width: 400 }}
                        allowClear
                    />

                    <Segmented
                        value={statusFilter}
                        onChange={(val) => {
                            setStatusFilter(val as StatusFilter);
                            setCurrentPage(1);
                        }}
                        options={[
                            { label: 'All', value: 'all' },
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' },
                        ]}
                    />
                </div>
                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={handleCreateNewVersionClick}
                >
                    Create New PageVersion
                </Button>
            </Space>

            {loading ? (
                <Skeleton active />
            ) : (
                <Table
                    dataSource={pageVersions}
                    columns={columns}
                    rowKey="guid"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalPageVersions,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={handleTableChange}
                />
            )}
            <StoresListModal
                open={storesModalVisible}
                onClose={handleCloseStoresModal}
                pageVersionGuid={selectedPageVersionGuid}
            />
            <CreatePageVersionForm
                open={isCreateDrawerVisible}
                onClose={() => setIsCreateDrawerVisible(false)}
                onCreate={handleCreateNewVersion}
            />
            <PageVersionDuplicateDrawer
                open={isDuplicateDrawerVisible}
                sourcePageVersionId={duplicateSourceGuid}
                sourcePageVersionName={duplicateSourceName}
                onClose={() => setIsDuplicateDrawerVisible(false)}
            />
        </div>
    );
};

export default PageVersions;
